import React, { useState, useEffect, useRef, useCallback } from 'react';
import { bankService, PublicBankInfo } from '../../services/bankService';
import { getApiBaseUrl } from '../../services/apiClient';
import { useTranslation } from '../../i18n';

interface BankLogosCarouselProps {
    countryCode?: string;
    /** Pixels per second scroll speed */
    speed?: number;
}

/**
 * Constructs the full logo URL.
 * If the logoUrl is a relative path (starts with /), prepends the API base URL.
 * If it's already absolute (starts with http), returns as-is.
 */
const getFullLogoUrl = (logoUrl: string): string => {
    if (!logoUrl) return `${getApiBaseUrl()}/images/banks/default-bank.png`;
    if (logoUrl.startsWith('http') || logoUrl.startsWith('data:')) {
        return logoUrl;
    }
    return `${getApiBaseUrl()}${logoUrl}`;
};

// ============================================================================
// InfiniteMarquee — JS-driven seamless infinite scroll
// ============================================================================
// Renders the bank list 3× back-to-back, measures the pixel width of one set,
// and uses requestAnimationFrame to translate. When the offset exceeds one
// set's width it snaps back by exactly that amount — the user sees zero jump
// because the second copy is visually identical to the first.
// ============================================================================

interface MarqueeProps {
    banks: PublicBankInfo[];
    speed: number;
}

const LOGO_WIDTH = 192; // w-48 = 12rem = 192px
const LOGO_GAP = 32;    // gap-8 = 2rem = 32px

const InfiniteMarquee: React.FC<MarqueeProps> = ({ banks, speed }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const offsetRef = useRef(0);
    const rafRef = useRef<number>(0);
    const lastTimeRef = useRef<number>(0);
    const pausedRef = useRef(false);
    const setWidthRef = useRef(0);

    // Calculate exact pixel width of one set: N logos × width + (N-1) gaps + trailing gap
    useEffect(() => {
        setWidthRef.current = banks.length * LOGO_WIDTH + banks.length * LOGO_GAP;
    }, [banks.length]);

    const animate = useCallback((timestamp: number) => {
        if (!lastTimeRef.current) lastTimeRef.current = timestamp;
        const delta = timestamp - lastTimeRef.current;
        lastTimeRef.current = timestamp;

        if (!pausedRef.current && setWidthRef.current > 0) {
            offsetRef.current += (speed * delta) / 1000;

            // When we've scrolled past one full set, snap back
            if (offsetRef.current >= setWidthRef.current) {
                offsetRef.current -= setWidthRef.current;
            }

            if (trackRef.current) {
                trackRef.current.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
            }
        }

        rafRef.current = requestAnimationFrame(animate);
    }, [speed]);

    useEffect(() => {
        rafRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafRef.current);
    }, [animate]);

    const handleMouseEnter = () => { pausedRef.current = true; };
    const handleMouseLeave = () => {
        pausedRef.current = false;
        lastTimeRef.current = 0; // prevent jump after long pause
    };

    // Render 3 copies to guarantee the viewport is always full
    const renderSet = (keyPrefix: string, ariaHidden: boolean) => (
        <div
            className="flex shrink-0 items-center"
            aria-hidden={ariaHidden || undefined}
            style={{ gap: `${LOGO_GAP}px`, paddingRight: `${LOGO_GAP}px` }}
        >
            {banks.map((bank) => (
                <a
                    key={`${keyPrefix}-${bank.bankId}`}
                    href={bank.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={ariaHidden ? -1 : 0}
                    className="flex-shrink-0 flex items-center justify-center transition-transform duration-300 hover:scale-110"
                    style={{ width: `${LOGO_WIDTH}px`, height: '96px' }}
                >
                    <img
                        src={getFullLogoUrl(bank.logoUrl)}
                        alt={ariaHidden ? '' : bank.bankName}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                            e.currentTarget.src = `${getApiBaseUrl()}/images/banks/default-bank.png`;
                        }}
                    />
                </a>
            ))}
        </div>
    );

    return (
        <div
            className="relative overflow-hidden"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Fade edges */}
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white/50 to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white/50 to-transparent z-10 pointer-events-none" />

            <div
                ref={trackRef}
                className="flex will-change-transform"
                style={{ transform: 'translate3d(0, 0, 0)' }}
            >
                {renderSet('a', false)}
                {renderSet('b', true)}
                {renderSet('c', true)}
            </div>
        </div>
    );
};

// ============================================================================
// BankLogosCarousel — main exported component
// ============================================================================

export const BankLogosCarousel: React.FC<BankLogosCarouselProps> = ({
    countryCode = 'DO',
    speed = 50,
}) => {
    const { t } = useTranslation();
    const [banks, setBanks] = useState<PublicBankInfo[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                // Check for demo mode secret in page URL (?secret=...)
                const urlParams = new URLSearchParams(window.location.search);
                const secret = urlParams.get('secret') || undefined;

                // Skip the API call entirely unless demo mode is active.
                // No banks have carouselVisible=true yet — the fetch always
                // returns an empty list and the component renders null.
                // This keeps /DO/carousel off the critical path (~1,500ms saved).
                //
                // TODO: Remove this guard when the first bank partnership is
                //       signed and carouselVisible is set to true in the DB.
                if (!secret) {
                    setLoading(false);
                    return;
                }

                const response = await bankService.getCarouselBanksByCountry(countryCode, secret);
                setBanks(response.banks || []);
            } catch (error) {
                console.error('Failed to load carousel banks:', error);
                setBanks([]);
            } finally {
                setLoading(false);
            }
        };

        fetchBanks();
    }, [countryCode]);

    // Need at least 2 banks to display
    if (loading || banks.length <= 1) {
        return null;
    }

    // Determine grid layout for small bank counts
    const getGridClass = () => {
        if (banks.length === 2) return 'sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2';
        if (banks.length === 3) return 'sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
        return 'sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    };

    const useScrollAnimation = banks.length > 4;

    return (
        <div className="w-full overflow-hidden bg-white/50 backdrop-blur-sm rounded-lg py-6 px-4">
            <h2 className="text-center text-black mb-8 fontbmedium4">
                {t('landing.banks.trustedBy')}
            </h2>

            {useScrollAnimation ? (
                <InfiniteMarquee banks={banks} speed={speed} />
            ) : (
                <div className={`grid ${getGridClass()} gap-8 max-w-5xl mx-auto`}>
                    {banks.map((bank) => (
                        <a
                            key={bank.bankId}
                            href={bank.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center h-32 p-6 transition-all duration-300 hover:scale-110"
                            title={`Visit ${bank.bankName}`}
                        >
                            <img
                                src={getFullLogoUrl(bank.logoUrl)}
                                alt={bank.bankName}
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                    e.currentTarget.src = `${getApiBaseUrl()}/images/banks/default-bank.png`;
                                }}
                            />
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BankLogosCarousel;